import { useState, useEffect, useCallback } from 'react';
import { Activity, X, Filter, User, RefreshCw, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { formatDistanceToNow } from 'date-fns';

/**
 * GlobalActivityFeed — floating right sidebar showing recent actions
 * Open: click toolbar button (header)
 */

const ACTION_COLORS = {
  create: 'bg-success/20 text-success border-success/30',
  update: 'bg-info/20 text-info border-info/30',
  delete: 'bg-error/20 text-error border-error/30',
  login: 'bg-slate-100 text-slate-700 border-slate-200',
  default: 'bg-slate-100 text-slate-600 border-slate-200',
};

const filterOptions = [
  { key: 'all', label: 'All' },
  { key: 'create', label: 'Created' },
  { key: 'update', label: 'Updated' },
  { key: 'delete', label: 'Deleted' },
];

export function GlobalActivityFeed({ open, onOpenChange }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetch = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await api.get('/activity-logs', { params: { limit: 60, action: filter === 'all' ? undefined : filter } });
      setLogs(res.data?.logs || res.data || []);
    } catch (e) {
      // 403 (no permission) or network errors: empty feed, but surface a hint
      setLogs([]);
    }
    setLoading(false);
  }, [open, filter]);

  useEffect(() => { fetch(); }, [fetch]);

  // Auto-refresh every 30s while open
  useEffect(() => {
    if (!open) return;
    const i = setInterval(fetch, 30000);
    return () => clearInterval(i);
  }, [open, fetch]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-[9100] animate-fade-up" onClick={() => onOpenChange(false)} data-testid="activity-feed-backdrop" />
      <div className="fixed top-0 right-0 bottom-0 w-full sm:w-96 bg-white z-[9101] shadow-2xl flex flex-col animate-fade-up" data-testid="activity-feed">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
              <Activity size={16} />
            </div>
            <div>
              <h2 className="text-sm font-semibold">Activity Feed</h2>
              <p className="text-[11px] text-white/60">Real-time actions across your team</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={fetch} title="Refresh" className="p-1.5 rounded hover:bg-white/10" data-testid="activity-refresh">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={() => onOpenChange(false)} className="p-1.5 rounded hover:bg-white/10" data-testid="activity-close">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/40 flex items-center gap-1 overflow-x-auto">
          <Filter size={12} className="text-slate-400 shrink-0" />
          {filterOptions.map(o => (
            <button
              key={o.key}
              onClick={() => setFilter(o.key)}
              data-testid={`activity-filter-${o.key}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors whitespace-nowrap ${filter === o.key ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
            >
              {o.label}
            </button>
          ))}
        </div>

        {/* Feed */}
        <div className="flex-1 overflow-y-auto" data-testid="activity-list">
          {loading && !logs.length ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex gap-3">
                  <div className="skeleton w-8 h-8 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <div className="skeleton h-3 w-3/4" />
                    <div className="skeleton h-2.5 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : !logs.length ? (
            <div className="p-12 text-center">
              <Activity className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-600 mb-1">No activity yet</p>
              <p className="text-xs text-slate-400">Actions will appear here as you and your team work.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {logs.map(l => {
                const colorClass = ACTION_COLORS[(l.action || '').toLowerCase()] || ACTION_COLORS.default;
                const userName = l.user_name || l.user_email || 'System';
                const initials = userName.split(/[.@\s]/).filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase();
                const entityLabel = l.resource || l.entity_type || '-';
                const detailText = l.description || l.message
                  || (l.details?.name ? `${l.action} ${entityLabel}: ${l.details.name}` : null)
                  || (l.entity_name ? `${l.action} ${entityLabel}: ${l.entity_name}` : null)
                  || (l.resource_id ? `${l.action} ${entityLabel} #${String(l.resource_id).slice(0, 8)}` : `${l.action} ${entityLabel}`);
                const ts = l.timestamp || l.created_at;
                return (
                  <li key={l.id || l._id || Math.random()} className="px-4 py-3 hover:bg-slate-50/80 transition-colors" data-testid="activity-item">
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-[11px] font-bold shrink-0">
                        {initials || <User size={14} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 justify-between">
                          <div className="min-w-0">
                            <p className="text-xs font-medium text-slate-800 truncate">{userName}</p>
                            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${colorClass}`}>{(l.action || 'EVENT').toUpperCase()}</span>
                              <span className="text-[10px] text-slate-500">{entityLabel}</span>
                            </div>
                          </div>
                          <span className="text-[10px] text-slate-400 shrink-0">
                            {ts ? formatDistanceToNow(new Date(ts), { addSuffix: true }) : ''}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1 line-clamp-2">{detailText}</p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer — link to full page */}
        <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50/30">
          <a href="/activity-logs" className="flex items-center justify-between text-[11px] font-medium text-slate-600 hover:text-success transition-colors" data-testid="activity-view-all">
            View full activity log <ChevronRight size={12} />
          </a>
        </div>
      </div>
    </>
  );
}
